import { Box, Checkbox, Grid, Skeleton, Typography } from "@mui/material";
import { NewCampaignContext } from "contexts/NewCampaign";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext } from "contexts/Subscription";
import { forwardRef, useContext } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { BentoBrand, SearchParams } from "schemas/dashboard";
import { routes } from "schemas/routes";

import CampaignBrandCard from "./CampaignBrandCard";
import styles from "./styles";

interface Props {
  brandsLoading: boolean;
  fetchMoreLoading: boolean;
  brands: BentoBrand[];
  numberSelected: number;
  ref: any;
  total: number;
  isRepitching?: boolean;
  hasActiveFilters?: boolean;
  sourceLabel?: string;
}
const REF_INDEX = 9; // The index used to start loading more recommendations

export default forwardRef<HTMLDivElement, Props>(
  (
    {
      brandsLoading,
      fetchMoreLoading,
      numberSelected,
      brands,
      total,
      isRepitching = false,
      hasActiveFilters = false,
      sourceLabel,
    },
    ref,
  ) => {
    const [searchParams] = useSearchParams();
    const {
      selectAll,
      selectedBrandIds,
      setSelectedBrandIds,
      excludedBrandIds,
      setExcludedBrandIds,
    } = useContext(NewCampaignContext);
    const { subscription } = useContext(SubscriptionContext);

    const brandQuery = searchParams.get(SearchParams.QUERY_DISCOVER);
    const { excludedBentoSearchFilterIds, searchFilters } = useContext(
      OrganizationUserContext,
    );

    const isExcludedKeyWords = () => {
      if (!excludedBentoSearchFilterIds) return false;
      for (const excludedFilterId of excludedBentoSearchFilterIds) {
        const matchedFilter = searchFilters?.find(
          (f) => f.id === excludedFilterId,
        );
        if (matchedFilter) {
          const inMatchedFilter = matchedFilter?.keywords?.find(
            (x) => x === brandQuery?.toLowerCase(),
          );
          if (inMatchedFilter) return true;
        }
      }
      return false;
    };

    const handleSelect = (bentoBrand: BentoBrand) => {
      if (selectAll) {
        setExcludedBrandIds((prev) => {
          const copy = new Set(prev);
          if (copy.has(bentoBrand.id)) {
            copy.delete(bentoBrand.id);
          } else {
            copy.add(bentoBrand.id);
          }
          return copy;
        });
        return;
      }
      setSelectedBrandIds((prev) => {
        const copy = new Set(prev);
        if (copy.has(bentoBrand.id)) {
          copy.delete(bentoBrand.id);
        } else {
          copy.add(bentoBrand.id);
        }

        return copy;
      });
    };

    const isSelected = (bentoBrand: BentoBrand, idx: number) => {
      if (idx < subscription?.remainingMessages! && selectAll) {
        return !excludedBrandIds.has(bentoBrand.id);
      }
      return selectedBrandIds.has(bentoBrand.id);
    };

    return (
      <Grid item container rowGap={1}>
        {!brandsLoading && !fetchMoreLoading && brands.length === 0 && (
          <Grid container item xs={12} rowGap={2}>
            {isExcludedKeyWords() ? (
              <Grid sx={styles.excludedContainer}>
                <Box
                  component="i"
                  className="fa-regular fa-magnifying-glass"
                  sx={{ ...styles.emptyStateIcon, mb: 1.5 }}
                />
                <Typography
                  sx={{ fontSize: 16, mb: 1, color: "text.secondary" }}
                >
                  No results found.
                </Typography>
                <Box sx={styles.emptyStateText}>
                  It appears you have excluded the keyword{" "}
                  <strong>
                    <em>{brandQuery}</em>
                  </strong>{" "}
                  from your recommendation preferences. To change this, go to
                  your <Link to={`/${routes.profile}`}>Profile page</Link> and
                  update the excluded keyword filters at the{" "}
                  <strong>Let us tailor your recommendations</strong> section.
                </Box>
              </Grid>
            ) : (
              <Grid item xs={12}>
                <Box sx={styles.emptyState}>
                  <Box
                    component="i"
                    className="fa-regular fa-magnifying-glass"
                    sx={styles.emptyStateIcon}
                  />
                  <Typography sx={styles.emptyStateText}>
                    {hasActiveFilters ? (
                      "No results found. Try changing the filters above to see more results."
                    ) : isRepitching ? (
                      <>
                        You don&apos;t have any brands to repitch
                        {sourceLabel && (
                          <>
                            {" "}
                            in <strong>“{sourceLabel}”</strong>
                          </>
                        )}{" "}
                        right now — these are brands you last reached out to
                        over 30 days ago who never replied.
                      </>
                    ) : (
                      <>
                        There are no brands available to pitch
                        {sourceLabel ? (
                          <>
                            {" "}
                            in <strong>“{sourceLabel}”</strong>
                          </>
                        ) : (
                          " here"
                        )}
                        .
                      </>
                    )}
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        )}

        {brandsLoading && (
          <Grid
            container
            item
            xs={12}
            justifyContent={"center"}
            sx={styles.skeletonContainer}
          >
            {Array.from({ length: 20 }, (x, i) => (
              <Skeleton
                sx={{ mb: 2 }}
                key={i}
                variant="rounded"
                width={"100%"}
                height={100}
              />
            ))}
          </Grid>
        )}

        <Grid container item xs={12} justifyContent="center" rowGap={1}>
          {!brandsLoading &&
            brands?.map((brand: BentoBrand, idx: number) => (
              <CampaignBrandCard
                ref={
                  (
                    brands.length > REF_INDEX
                      ? idx === brands.length - REF_INDEX
                      : idx === brands.length - 1
                  )
                    ? ref
                    : undefined
                }
                key={`brand-card-${brand.id}-${idx}`}
                brand={brand}
                actions={
                  <Checkbox
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => {
                      handleSelect(brand);
                    }}
                    disableRipple
                    checked={isSelected(brand, idx)}
                    icon={
                      <Box
                        sx={{ color: "green.main" }}
                        component="i"
                        className="fa-regular fa-square"
                      />
                    }
                    checkedIcon={
                      <Box
                        component="i"
                        className="fa-solid fa-square-check"
                        sx={{ color: "green.main" }}
                      />
                    }
                  />
                }
              />
            ))}
        </Grid>

        {fetchMoreLoading && (
          <Grid
            container
            item
            xs={12}
            justifyContent={"center"}
            sx={styles.skeletonContainer}
          >
            {Array.from({ length: 3 }, (x, i) => (
              <Skeleton
                sx={{ mb: 2 }}
                key={i}
                variant="rounded"
                width={"100%"}
                height={100}
              />
            ))}
          </Grid>
        )}
      </Grid>
    );
  },
);
